import { useState, useEffect, useRef } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  IconButton,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import { trackEvent } from "../../../configureMixpanel";

const FileUploadForm = ({ projectId, onSuccess, onError, api, disabled = false }) => {
  const [files, setFiles] = useState([]); // Array of { file, schemaId }
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const [schemas, setSchemas] = useState([]);
  const [schemasLoading, setSchemasLoading] = useState(true);

  useEffect(() => {
    const fetchSchemas = async () => {
      try {
        setSchemasLoading(true);
        const data = await api.fetchFileTemplates(projectId);
        setSchemas(data);
      } catch (error) {
        console.error("Error fetching schemas:", error);
        if (onError) {
          onError(error.message || "Failed to load file templates");
        }
      } finally {
        setSchemasLoading(false);
      }
    };

    fetchSchemas();
  }, []);

  const resetFiles = () => {
    setFiles([]);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleFileSelect = (e) => {
    const newFiles = Array.from(e.target.files || []);
    // Add new files without a schema assignment yet
    setFiles([...files, ...newFiles.map((file) => ({ file, schemaId: "" }))]);
  };

  const handleRemoveFile = (index) => {
    setFiles(files.filter((_, i) => i !== index));
  };

  const handleSchemaChange = (index, schemaId) => {
    const updatedFiles = [...files];
    updatedFiles[index].schemaId = schemaId;
    setFiles(updatedFiles);
  };

  const handleUpload = async () => {
    // Validate all files have a schema selected
    if (files.length === 0) {
      onError("Please add files to upload");
      return;
    }

    const filesWithoutSchema = files.filter((f) => !f.schemaId);
    if (filesWithoutSchema.length > 0) {
      onError("Please select a file template for all files");
      return;
    }

    onError("");
    setUploading(true);
    const fileCount = files.length;
    try {
      let totalSuccesses = 0;
      let totalFailures = 0;
      const allFailures = [];

      for (const { file, schemaId } of files) {
        const report = await api.uploadFile(schemaId, file);

        if (report && typeof report === "object") {
          const successes = Array.isArray(report.successes)
            ? report.successes
            : [];
          const failures = Array.isArray(report.failures)
            ? report.failures
            : [];

          totalSuccesses += successes.length;
          totalFailures += failures.length;
          allFailures.push(...failures);
        } else {
          resetFiles();
          onError("Request not valid. Please contact support.");
          trackEvent("file_upload_submitted", {
            outcome: "error",
            file_count: fileCount,
            success_count: totalSuccesses,
            failure_count: totalFailures,
          });
          return;
        }
      }

      resetFiles();

      // Show summary based on results
      if (totalFailures > 0) {
        const summary =
          totalSuccesses > 0
            ? `Partial upload: ${totalSuccesses} file(s) succeeded, ${totalFailures} file(s) rejected`
            : `Upload failed: All ${totalFailures} files were rejected`;

        trackEvent("file_upload_submitted", {
          outcome: totalSuccesses > 0 ? "partial_failure" : "full_failure",
          file_count: fileCount,
          success_count: totalSuccesses,
          failure_count: totalFailures,
        });

        onError(
          <>
            <div>{summary}</div>
            {allFailures.map((f, i) => (
              <Box key={i} sx={{ mt: 1 }}>
                <div>
                  <strong>{f.name || "Unknown file"}</strong>
                </div>
                {(f.reasons && Array.isArray(f.reasons)
                  ? f.reasons
                  : ["Unknown error"]
                ).map((reason, j) => (
                  <div key={j}>{reason}</div>
                ))}
              </Box>
            ))}
          </>
        );
      } else {
        trackEvent("file_upload_submitted", {
          outcome: "success",
          file_count: fileCount,
          success_count: totalSuccesses,
          failure_count: totalFailures,
        });
        onSuccess();
      }
    } catch (error) {
      resetFiles();
      onError(error.message || "Failed to upload files");
      trackEvent("file_upload_submitted", {
        outcome: "error",
        file_count: fileCount,
      });
    } finally {
      setUploading(false);
    }
  };

  if (schemasLoading) return <CircularProgress />;

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Upload Files
        </Typography>

        <Box sx={{ mb: 3 }}>
          <Button
            variant="outlined"
            component="label"
            fullWidth
            sx={{ mb: 2 }}
            disabled={disabled}
          >
            Select Files
            <input
              ref={fileInputRef}
              type="file"
              multiple
              hidden
              onChange={handleFileSelect}
            />
          </Button>

          {files.length > 0 && (
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 2 }}>
                Selected Files ({files.length}):
              </Typography>
              <List dense>
                {files.map((fileItem, index) => (
                  <ListItem
                    key={index}
                    sx={{
                      flexDirection: "column",
                      alignItems: "flex-start",
                      py: 2,
                      borderBottom: "1px solid #e0e0e0",
                    }}
                    secondaryAction={
                      <IconButton
                        edge="end"
                        size="small"
                        onClick={() => handleRemoveFile(index)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    }
                  >
                    <ListItemText
                      primary={fileItem.file.name}
                      sx={{ mb: 1, pr: 4 }}
                    />
                    <FormControl sx={{ minWidth: 300 }} size="small">
                      <InputLabel>File Template</InputLabel>
                      <Select
                        value={fileItem.schemaId}
                        onChange={(e) =>
                          handleSchemaChange(index, e.target.value)
                        }
                        label="File Template"
                      >
                        {schemas.map((schema) => (
                          <MenuItem key={schema.id} value={schema.id}>
                            {schema.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </ListItem>
                ))}
              </List>
            </Box>
          )}
        </Box>

        <Button
          variant="contained"
          startIcon={<CloudUploadIcon />}
          onClick={handleUpload}
          disabled={disabled || files.length === 0 || uploading}
          fullWidth
        >
          {uploading ? <CircularProgress size={24} /> : "Upload Files"}
        </Button>
      </CardContent>
    </Card>
  );
};

export default FileUploadForm;
